if (!customElements.get('product-stock-locations')) {
  customElements.define(
    'product-stock-locations',
    class ProductStockLocations extends HTMLElement {
      constructor() {
        super();
        this._wireToggle();
      }

      connectedCallback() {
        this._wireToggle();
        if (this.dataset.variantId) {
          this._fetchProxy(this.dataset.variantId);
        }
      }

      _wireToggle() {
        const toggle = this.querySelector('[data-stock-toggle]');
        if (!toggle || toggle.__wired) return;
        toggle.__wired = true;
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          const items = this.querySelectorAll('.stock-locations__item.is-hidden, .stock-locations__item.is-shown');
          items.forEach((it) => {
            if (expanded) {
              it.classList.add('is-hidden');
              it.classList.remove('is-shown');
            } else {
              it.classList.remove('is-hidden');
              it.classList.add('is-shown');
            }
          });
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          toggle.textContent = expanded
            ? toggle.dataset.labelMore
            : toggle.dataset.labelLess;
        });
      }

      _params() {
        const p = new URLSearchParams();
        p.set('hide_unavailable', this.dataset.hideUnavailable || 'false');
        p.set('show_address', this.dataset.showAddress || 'true');
        p.set('collapsed', this.dataset.collapsed || 'false');
        p.set('initial_count', this.dataset.initialCount || '4');
        if (this.dataset.title) p.set('title', this.dataset.title);
        if (this.dataset.suffixStore) p.set('suffix_store', this.dataset.suffixStore);
        if (this.dataset.suffixWarehouse) p.set('suffix_warehouse', this.dataset.suffixWarehouse);
        return p.toString();
      }

      fetchAvailability(variantId) {
        if (!variantId) return;
        this.dataset.variantId = variantId;
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';

        const url = `${rootUrl}variants/${variantId}/?section_id=stock-locations&${this._params()}`;

        this.classList.add('is-loading');

        fetch(url)
          .then((r) => r.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const fragment = doc.querySelector('.shopify-section');
            if (!fragment) return;
            const inner = fragment.querySelector('.stock-locations__inner');
            if (inner) {
              const current = this.querySelector('.stock-locations__inner');
              if (current) {
                current.replaceWith(inner);
              } else {
                this.appendChild(inner);
              }
              this._wireToggle();
            }
          })
          .catch(() => {
            // Soft fail – keep the previous render.
          })
          .finally(() => {
            this.classList.remove('is-loading');
            this._fetchProxy(variantId);
          });
      }

      _fetchProxy(variantId) {
        const proxyUrl = (this.dataset.proxyUrl || '').trim();
        if (!proxyUrl || !variantId) return;

        const sep = proxyUrl.includes('?') ? '&' : '?';
        const url = `${proxyUrl}${sep}variant_id=${encodeURIComponent(variantId)}`;

        fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data || !Array.isArray(data.locations)) return;
            this._applyProxyData(data.locations, variantId);
          })
          .catch(() => {
            // Soft fail – keep the Liquid-rendered fallback (Disponible / Sin stock).
          });
      }

      _applyProxyData(locations, variantId) {
        if (this.dataset.variantId !== String(variantId)) return;

        const lowThreshold = parseInt(this.dataset.lowThreshold || '5', 10);
        const items = Array.from(this.querySelectorAll('.stock-locations__item'));
        if (items.length === 0) return;

        const byId = new Map();
        const byName = new Map();
        items.forEach((it) => {
          if (it.dataset.locationId) byId.set(String(it.dataset.locationId), it);
          if (it.dataset.locationName) byName.set(it.dataset.locationName.trim().toLowerCase(), it);
        });

        locations.forEach((loc) => {
          let item = null;
          if (loc.id != null && byId.has(String(loc.id))) {
            item = byId.get(String(loc.id));
          } else if (loc.name) {
            item = byName.get(loc.name.trim().toLowerCase());
          }
          if (!item) return;

          const qty = Number(loc.available);
          if (Number.isNaN(qty)) return;

          this._updateItemTag(item, qty, lowThreshold);
        });
      }

      _updateItemTag(item, qty, lowThreshold) {
        const tag = item.querySelector('.stock-locations__tag');
        if (!tag) return;

        tag.classList.remove(
          'stock-locations__tag--ok',
          'stock-locations__tag--low',
          'stock-locations__tag--out'
        );

        if (qty <= 0) {
          tag.classList.add('stock-locations__tag--out');
          tag.textContent = 'Sin stock';
          item.classList.add('is-unavailable');
        } else if (qty <= lowThreshold) {
          tag.classList.add('stock-locations__tag--low');
          tag.textContent = `${qty} uds`;
          item.classList.remove('is-unavailable');
        } else {
          tag.classList.add('stock-locations__tag--ok');
          tag.textContent = `${qty} uds`;
          item.classList.remove('is-unavailable');
        }
      }
    }
  );
}
